import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AttachmentPayload, ChatTheme, ParticipantRole, TextPayload } from '@kairelo/shared';
import { useConversation, type DecryptedMessage } from '../lib/useConversation.js';
import {
  createTranslator,
  detectCapability,
  preferredLanguage,
  type TranslationAvailability,
  type Translator,
} from '../lib/translation.js';
import { ChatHeader, type HeaderAction } from './ChatHeader.tsx';
import { Composer } from './Composer.tsx';
import { MessageList } from './MessageList.tsx';
import { G } from './icons.tsx';
import type { ChatMessage } from './types.js';
import { log } from '../lib/logging.js';
import { useT } from '../i18n/I18n.tsx';

/**
 * An open conversation: header, messages and composer, wired to the realtime
 * session.
 *
 * Presentation only — encryption, transport and lifecycle all live in
 * `useConversation`, which this component does not reach past. That separation
 * is what lets a second interface theme reuse the whole pane.
 */

export interface ChatPaneProps {
  conversationId: string;
  role: ParticipantRole;
  token?: string;
  encryptionKey: CryptoKey | null;
  title: string;
  onBack?: () => void;
  onReport: () => void;
  onShowAbout: () => void;
  /** Extra items for the overflow menu, e.g. owner-only actions. */
  extraMenu?: HeaderAction[];
  /** Owner branding, applied by the caller through the theme provider. */
  theme?: ChatTheme;
  /**
   * Language incoming messages are translated into.
   *
   * Supplied by the caller: an owner's saved preference, or the browser's
   * language for a visitor, who has no account to store one on.
   */
  language?: string;
}

export function ChatPane({
  conversationId,
  role,
  token,
  encryptionKey,
  title,
  onBack,
  onReport,
  onShowAbout,
  extraMenu,
  language,
}: ChatPaneProps) {
  const t = useT();
  const conversation = useConversation({
    conversationId,
    role,
    ...(token ? { token } : {}),
    key: encryptionKey,
  });

  const [error, setError] = useState<string | null>(null);

  /* --------------------------------------------------------- translation */

  const targetLanguage = useMemo(() => language ?? preferredLanguage(), [language]);
  const [availability, setAvailability] = useState<TranslationAvailability>('unavailable');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const translatorRef = useState<{ current: Translator | null }>(() => ({ current: null }))[0];

  const translationOn = availability !== 'unavailable';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const capability = await detectCapability({
        sourceLanguage: targetLanguage === 'en' ? 'es' : 'en',
        targetLanguage,
      });
      if (!cancelled) setAvailability(capability.availability);
    })();
    return () => {
      cancelled = true;
    };
  }, [targetLanguage]);

  useEffect(() => {
    conversation.announceTranslation(translationOn, availability);
  }, [availability, conversation, translationOn]);

  /**
   * Translation runs automatically wherever it is available (concept §8).
   * Someone reading in their own language should not have to know, or care,
   * which language the other person typed in.
   */
  const translate = useCallback(
    async (message: DecryptedMessage) => {
      if (!translationOn || message.kind !== 'text' || !message.payload) return;
      if (translations[message.id]) return;

      const payload = message.payload as TextPayload;
      const source = payload.lang?.split('-')[0] ?? 'en';
      if (source === targetLanguage) return;

      try {
        const current = translatorRef.current;
        if (
          !current ||
          current.sourceLanguage !== source ||
          current.targetLanguage !== targetLanguage
        ) {
          current?.destroy();
          translatorRef.current = await createTranslator({
            sourceLanguage: source,
            targetLanguage,
          });
        }
        const translator = translatorRef.current;
        if (!translator) return;
        const output = await translator.translate(payload.body);
        setTranslations((previous) => ({ ...previous, [message.id]: output }));
      } catch {
        log.debug('local translation failed');
      }
    },
    [targetLanguage, translationOn, translations, translatorRef],
  );

  useEffect(() => {
    if (!translationOn) return;
    for (const message of conversation.messages) {
      if (message.sender !== role) void translate(message);
    }
  }, [conversation.messages, role, translate, translationOn]);

  useEffect(
    () => () => {
      translatorRef.current?.destroy();
    },
    [translatorRef],
  );

  /* ------------------------------------------------------- view mapping */

  const messages: ChatMessage[] = useMemo(
    () =>
      conversation.messages.map((message) => {
        const attachment =
          message.payload &&
          message.payload.kind !== 'text' &&
          message.payload.kind !== 'visitor_name'
            ? (message.payload as AttachmentPayload)
            : undefined;

        return {
          id: message.id,
          seq: message.seq,
          sender: message.sender,
          kind: message.kind,
          createdAt: message.createdAt,
          delivery: message.delivery ?? 'sent',
          ...(message.payload?.kind === 'text'
            ? {
                body: (message.payload as TextPayload).body,
                lang: (message.payload as TextPayload).lang,
              }
            : {}),
          ...(translations[message.id] ? { translation: translations[message.id] } : {}),
          ...(attachment
            ? {
                attachment: {
                  filename: attachment.filename,
                  mimeType: attachment.mimeType,
                  size: attachment.size,
                  ...(attachment.caption ? { caption: attachment.caption } : {}),
                  ...(attachment.durationSeconds !== undefined
                    ? { durationSeconds: attachment.durationSeconds }
                    : {}),
                },
              }
            : {}),
          ...(message.unreadable ? { unreadable: true } : {}),
        } satisfies ChatMessage;
      }),
    [conversation.messages, translations],
  );

  const openAttachment = useCallback(
    async (message: ChatMessage) => {
      const source = conversation.messages.find((candidate) => candidate.id === message.id);
      if (!source) throw new Error('That attachment is no longer available.');
      const media = await conversation.openAttachment(source);
      return { url: media.url, mimeType: media.mimeType };
    },
    [conversation],
  );

  /* ---------------------------------------------------------------- view */

  const status = conversation.ended
    ? t('chat.conversationEnded')
    : conversation.status === 'connecting'
      ? t('chat.connecting')
      : conversation.status === 'open'
        ? conversation.peerTyping
          ? t('chat.typing')
          : conversation.presence.owner && conversation.presence.visitor
            ? t('chat.online')
            : t('chat.offline')
        : t('chat.reconnecting');

  const menu: HeaderAction[] = [
    { glyph: 'info', label: t('chat.about'), onSelect: onShowAbout },
    ...(extraMenu ?? []),
    { glyph: 'flag', label: t('chat.report'), onSelect: onReport, danger: true },
  ];

  return (
    <section className="msgr__chat">
      <ChatHeader
        name={title}
        status={
          conversation.peerTyping ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {t('chat.typing')}
              <span className="typing" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </span>
          ) : (
            status
          )
        }
        {...(onBack ? { onBack } : {})}
        onOpenProfile={onShowAbout}
        menu={menu}
      />

      <MessageList
        messages={messages}
        role={role}
        onOpenAttachment={openAttachment}
        banner={
          <>
            <div className="sys-msg">
              <G n="lock" size={13} /> {t('chat.encryptionBanner')}
            </div>
            {/*
              Shown whenever this browser holds no key — not only when the
              conversation happens to be empty. Unreadable bubbles with no
              explanation of why is exactly the state acceptance item 18 exists
              to prevent, and it is the state an owner on a second device is
              most likely to land in.
            */}
            {!encryptionKey ? (
              <div className="sys-msg sys-msg--warn">
                <G n="lock" size={13} /> {t('chat.emptyNoKey')}
              </div>
            ) : null}
          </>
        }
        emptyState={
          // Without a key the banner above already explains the situation, so
          // repeating it here would just say the same thing twice.
          encryptionKey ? <div className="chat-empty">{t('chat.emptyWithKey')}</div> : null
        }
      />

      {error || conversation.lastError ? (
        <div className="sys-msg" style={{ margin: '0 auto 6px', color: 'var(--ui-danger)' }}>
          {error ?? conversation.lastError}
        </div>
      ) : null}

      {conversation.ended ? (
        <div className="composer">
          <div className="composer__recording" style={{ justifyContent: 'center' }}>
            {t('chat.endedComposer')}
          </div>
        </div>
      ) : (
        <Composer
          disabled={!encryptionKey}
          disabledReason={t('composer.disabled')}
          onTyping={conversation.setTyping}
          onSendText={async (body) => {
            setError(null);
            conversation.clearError();
            try {
              await conversation.sendText(body, navigator.language);
            } catch (sendError) {
              setError(sendError instanceof Error ? sendError.message : t('composer.notConnected'));
            }
          }}
          onSendFiles={async (files) => {
            setError(null);
            conversation.clearError();
            for (const file of Array.from(files)) {
              try {
                await conversation.sendAttachment({
                  file,
                  filename: file.name,
                  mimeType: file.type || 'application/octet-stream',
                  kind: kindForFile(file),
                });
              } catch (uploadError) {
                setError(
                  uploadError instanceof Error ? uploadError.message : t('composer.fileFailed'),
                );
              }
            }
          }}
          onRecordVoice={(blob, seconds) => {
            void conversation
              .sendAttachment({
                file: blob,
                filename: `voice-message-${new Date().toISOString()}.webm`,
                mimeType: blob.type || 'audio/webm',
                kind: 'voice',
                durationSeconds: seconds,
              })
              .catch((recordError: unknown) =>
                setError(
                  recordError instanceof Error ? recordError.message : t('composer.voiceFailed'),
                ),
              );
          }}
        />
      )}
    </section>
  );
}

function kindForFile(file: File): 'photo' | 'video' | 'file' {
  const group = file.type.split('/')[0] ?? '';
  if (group === 'image') return 'photo';
  if (group === 'video') return 'video';
  return 'file';
}
